import { Link, useLocation } from "react-router-dom";
import { session } from "../../lib/session";
import { getWorkspace } from "../../lib/workspaceStore";
import { useMe } from "../../hooks/useQueries";
import { useDeviceProfile } from "../../hooks/useDeviceProfile";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

const defaultBranding = {
  brand_name: "OmniMark HQ",
  logo_url: null,
  accent_color: "#000000",
  white_label: false,
};

const nav = [
  { href: "/app", label: "Dashboard" },
  { href: "/app/workspaces", label: "Workspaces" },
  { href: "/app/brand", label: "Brand DNA" },
  { href: "/app/branding", label: "Branding", hideForClient: true },
  { href: "/app/ads", label: "Ad Creator" },
  { href: "/app/studio", label: "AI Studio" },
  { href: "/app/builder", label: "OmniCode Builder" },
  { href: "/app/avatars", label: "Avatar Library" },
  { href: "/app/studio/packs", label: "Pack Builder" },
  { href: "/app/studio/avatar", label: "Avatar Studio" },
  { href: "/app/clip-studio", label: "Clip Studio" },
  { href: "/app/content-calendar", label: "Content Calendar" },
  { href: "/app/campaigns", label: "Campaigns" },
  { href: "/app/dropshipping", label: "Dropshipping" },
  { href: "/app/connections", label: "Connections" },
  { href: "/app/website", label: "Website Builder", hideForClient: true },
  { href: "/app/marketplace", label: "App Directory", hideForClient: true },
  { href: "/app/members", label: "Members", hideForClient: true },
  { href: "/app/library", label: "Library" },
  { href: "/app/upgrade", label: "Upgrade", hideForClient: true },
  { href: "/app/admin", label: "Admin", adminOnly: true },
];

export function AppShell({ children }) {
  const location = useLocation();
  function signOut() {
    session.clear();
    // Drop account-specific React/query state as well as the stored session.
    window.location.assign("/login");
  }
  const pathname = location.pathname;
  const { data: user, isLoading: isLoadingMe } = useMe();
  const device = useDeviceProfile();
  
  const workspace = getWorkspace();
  const role = workspace?.role || "owner";
  const branding = workspace?.branding || defaultBranding;
  // Keep the administrative route out of customer navigation, while avoiding a
  // misleading "missing Admin" state during the short interval before /me has
  // returned after a sign-in or page refresh. Server-side admin authorization
  // remains the authority for the route and every admin API action.
  const isAdmin = Boolean(user?.is_admin) || session.isOwnerAdmin(user?.email);
  const compactShell = device.prefersCompactNav || device.width < 980;

  const filteredNav = nav.filter((item) => {
    if (role === "client" && item.hideForClient) return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  return (
    <div
      className="min-h-screen bg-muted/30"
      data-device-type={device.type}
      data-device-family={device.family}
      data-device-orientation={device.orientation}
      data-touch={device.touch ? "true" : "false"}
    >
      <div className="mx-auto flex min-h-screen w-full max-w-7xl min-w-0">
        {/* Sidebar */}
        <aside className={compactShell ? "hidden" : "hidden w-64 shrink-0 flex-col border-r bg-background p-4 md:flex"}>
          <div className="mb-4">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.brand_name} className="h-8 mb-1" />
            ) : (
              <div className="text-lg font-semibold" style={{ color: branding.accent_color }}>
                {branding.brand_name || defaultBranding.brand_name}
              </div>
            )}
            {!branding.white_label && (
              <div className="text-xs text-muted-foreground">AI Marketing Platform</div>
            )}
          </div>
          <Separator className="my-2" />
          <nav className="flex flex-col gap-1">
            {filteredNav.map((item) => {
              const active = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={[
                    "rounded-lg px-3 py-2 text-sm transition",
                    active ? "bg-muted font-medium" : "hover:bg-muted/60",
                  ].join(" ")}
                  style={active && branding.accent_color ? { borderLeft: `3px solid ${branding.accent_color}` } : {}}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="mt-auto pt-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={signOut}
            >
              Sign out
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 md:px-6">
              <div className="min-w-0">
                <div className="truncate text-sm text-muted-foreground">
                  {user?.email || session.getEmail() || (isLoadingMe ? "Restoring session…" : "Not signed in")}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {device.label} • {device.orientation}
                </div>
              </div>
              <Link to="/app/ads">
                <Button className="h-9 px-3 text-xs sm:text-sm" style={branding.accent_color ? { backgroundColor: branding.accent_color } : {}}>
                  Generate Ad Pack
                </Button>
              </Link>
              {compactShell && (
                <Button variant="outline" className="h-9 px-3 text-xs sm:text-sm" onClick={signOut}>
                  Sign out
                </Button>
              )}
            </div>
            {compactShell && (
              <nav className="flex gap-2 overflow-x-auto border-t px-4 py-2">
                {filteredNav.map((item) => {
                  const active = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={[
                        "shrink-0 rounded-full border px-3 py-2 text-xs font-medium",
                        active ? "bg-foreground text-background" : "bg-background text-foreground",
                      ].join(" ")}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            )}
          </header>

          <div className="min-w-0 p-3 sm:p-4 md:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
